import { useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import React, { useEffect, useState } from "react";
import {
  Outlet,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import Select from "react-select";
import { ModuleTabs } from "../../components/virtual/ModuleTabs";
import {
  Building2,
  Handshake,
  LayoutGrid,
  MapIcon,
  Search,
  Users,
} from "lucide-react";
import { toSlug } from "../../helper/toSlug";

import { FiMail, FiPhone, FiUsers, FiMapPin } from "react-icons/fi";
import { vURL } from "../../Url";
import MapComponent from "./MapComponent";
import MapMobile from "./MapMobile";
import useWindowWidth from "../../hooks/useWindowWidth";
const DEFAULT_FILTERS = {
  country: "",
  state: "",
  city: "",
};
const MODULES = [
  {
    key: "organizations",
    label: "Organization",
    icon: <Building2 size={16} />,
  },
  {
    key: "channel_partners",
    label: "VS Center",
    icon: <Handshake size={16} />,
  },
  { key: "sevaks", label: "Sevak", icon: <Users size={16} /> },
];

const DEFAULT_MODULE = "organization";
const VirtualLayout = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { module } = useParams(); // "organization" | "channel-partner" | "sevak"
  const navigate = useNavigate();

  const windowWidth = useWindowWidth();

  const isMobile = windowWidth < 1024;

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [viewMode, setViewMode] = useState("card");

  const countryParam = searchParams.get("country");
  const stateParam = searchParams.get("state");
  const cityParam = searchParams.get("city");

  const [selectedCountry, setSelectedCountry] = useState(
    searchParams.get("country") || DEFAULT_FILTERS.country,
  );
  const [selectedState, setSelectedState] = useState(
    stateParam ? Number(stateParam) : "",
  );

  const [selectedCity, setSelectedCity] = useState(
    cityParam ? Number(cityParam) : "",
  );

  const { data, isPending, error } = useQuery({
    queryKey: ["vURL", selectedCountry],
    queryFn: async () => {
      const res = await axios.get(`${vURL}/api/v1/public/countries`, {
        params: {
          country_id: selectedCountry,
        },
      });
      return res.data;
    },
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 1000 * 60 * 60, // 1 hour
    cacheTime: 1000 * 60 * 60 * 6, // 6 hours
  });

  const { data: statesData, isPending: loadingStates } = useQuery({
    queryKey: ["states", selectedCountry],
    queryFn: async () => {
      if (!selectedCountry) return {};
      const res = await axios.get(
        `${vURL}/api/v1/public/states/${selectedCountry}`,
      );
      return res.data;
    },
    enabled: !!selectedCountry, // 🔥 runs only when country exists
    staleTime: 1000 * 60 * 60,
  });

  const { data: citiesData } = useQuery({
    queryKey: ["cities", selectedState],
    queryFn: async () => {
      if (!selectedState) return {};
      const res = await axios.get(
        `${vURL}/api/v1/public/cities/${selectedState}`,
      );
      return res.data;
    },
    enabled: !!selectedState, // 🔥 runs only when state exists
    staleTime: 1000 * 60 * 60,
  });

  const countryOptions =
    data?.data &&
    data.data?.map((count) => ({
      value: count?.shortname,
      label: count?.name,
    }));

  const country_name = countryOptions?.find(
    (item) => item?.value === selectedCountry,
  );
;
  const stateOptions =
    statesData?.data &&
    statesData.data?.map((state) => ({
      value: state?.id,
      label: state?.name,
    }));

  const state_name = stateOptions?.find(
    (item) => item?.value === selectedState,
  );

  const cityOptions =
    citiesData?.data &&
    citiesData.data?.map((city) => ({
      value: city?.id,
      label: city?.name,
    }));

  const city_name = cityOptions?.find((item) => item?.value === selectedCity);

  const {
    data: listData,
    isPending: listLoading,
    isFetching: listFetch,
  } = useQuery({
    queryKey: [
      "list_sevak",
      module,
      debouncedSearch,
      country_name?.label,
      state_name?.label,
      city_name?.label,
    ], // 👈 add search here
    queryFn: async () => {
      const res = await axios.get(
        `https://virtualsevak.com/api/v1/public/${module}`,
        {
          params: {
            ...(debouncedSearch ? { search: debouncedSearch } : {}),
            country_name: country_name?.label,
            state_name: state_name?.label,
            city_name: city_name?.label,
          },
        },
      );
      return res.data;
    },
    // enabled: !!module,
    // staleTime: 1000 * 60 * 60,
  });

  const updateView = (mode) => {
    setViewMode(mode);
  };

  const updateParam = (key, value, resetTree = true) => {
    // setPath([]);
    const params = new URLSearchParams(searchParams);

    if (value) {
      params.set(key, value);
    } else {
      params.delete(key);
    }
    if (resetTree) {
      params.delete("path");
      // setPath([]);
    }
    setSearchParams(params);
  };

  useEffect(() => {
    setSelectedCountry(countryParam ?? DEFAULT_FILTERS.country);
    setSelectedState(stateParam ? Number(stateParam) : "");
    setSelectedCity(cityParam ? Number(cityParam) : "");
  }, [countryParam, stateParam, cityParam]);

  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    let changed = false;

    if (!params.get("country")) {
      params.set("country", DEFAULT_FILTERS.country);
      changed = true;
    }
    if (!params.get("state")) {
      params.set("state", DEFAULT_FILTERS.state);
      changed = true;
    }
    if (!params.get("city")) {
      params.set("city", DEFAULT_FILTERS.city);
      changed = true;
    }
    if (changed) {
      setSearchParams(params, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const selectContainerDesktop = (base) => ({ ...base, width: 190 });
  const selectContainerMobile = (base) => ({
    ...base,
    width: "100%",
    minWidth: 0,
  });
  const portalStyles = {
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    menu: (base) => ({ ...base, zIndex: 9999 }),
  };

  const onModuleChange = (key) => {
    const qs = searchParams.toString(); // keep country/state/city...
    navigate(`/virtual-sevak/${key}${qs ? `?${qs}` : ""}`);
  };

  const location = useLocation();
  const isDetail = location.pathname.split("/").length >= 4;

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500); // 500ms debounce

    return () => clearTimeout(timer);
  }, [search]);

  // Country Detect-----

  const { data: detectedCountry } = useQuery({
    queryKey: ["detect_country"],
    queryFn: async () => {
      const res = await fetch("https://ipapi.co/json/");
      const data = await res.json();
      return data?.country_code || null;
    },
    staleTime: Infinity, // never refetch
    cacheTime: Infinity,
    retry: false,
  });

  useEffect(() => {
    const currentCountry = searchParams.get("country");

    if (!currentCountry && detectedCountry) {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          params.set("country", detectedCountry);
          return params;
        },
        { replace: true },
      );
    }
  }, [detectedCountry, searchParams, setSearchParams]);

  return (
    <div
      // onContextMenu={(e) => e.preventDefault()}
      onCopy={(e) => e.preventDefault()}
      onPaste={(e) => e.preventDefault()}
      onCut={(e) => e.preventDefault()}
      onDragStart={(e) => e.preventDefault()}
      className=" select-none"
    >
      {/* Top Feature Bar */}

      <div className="z-40 border-b border-orange-100 bg-gradient-to-b from-orange-50/80 to-white/80 backdrop-blur virtual">
        <div className="mx-auto max-w-[1380px] px-2  max-md:py-2.5">
          <div className="rounded-2xl max-md:border max-md:border-orange-100 max-mdbg-white/70 max-md:shadow-[0_10px_30px_rgba(0,0,0,0.06)]">
            <div className="p-2 sm:p-3">
              {/* ✅ ONE ROW (Desktop) */}
              <div className="flex items-center justify-between flex-col lg:flex-row gap-3">
                {/* Left: Tabs (NOT centered) */}
                <div className="shrink-0">
                  <ModuleTabs
                    value={module}
                    MODULES={MODULES}
                    onChange={(m) => {
                      onModuleChange(m);
                      setSearch('')
                      //   setSelectedModule(m);
                      //   updateModule(m);
                    }}
                  />
                </div>

                {/* Right: Filters */}
                <div className="hidden md:grid grid-cols-3 lg:flex flex-wrap justify-end gap-2">
                  {/* Country */}
                  <Select
                    classNamePrefix="custom-select"
                    options={countryOptions}
                    value={countryOptions?.find(
                      (c) => c.value === selectedCountry,
                    )}
                    onChange={(val) => {
                      const country = val?.value || "";
                      setSelectedCountry(country);
                      setSelectedState("");
                      setSelectedCity("");
                      // navigate(`/virtual-sevak/${module}/${country}`);
                      const params = new URLSearchParams(searchParams);
                      params.set("country", country);
                      params.delete("state");
                      params.delete("city");
                      params.delete("path");
                      setSearchParams(params);
                    }}
                    placeholder="Country"
                    isSearchable
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={{
                      container: selectContainerDesktop,
                      ...portalStyles,
                    }}
                  />

                  {/* State */}
                  <Select
                    classNamePrefix="custom-select"
                    options={stateOptions}
                    value={
                      stateOptions?.find(
                        (c) => c.value === Number(selectedState),
                      ) || null
                    }
                    onChange={(val) => {
                      const state = val?.value || "";
                      setSelectedState(state);
                      setSelectedCity("");

                      const params = new URLSearchParams(searchParams);
                      params.set("state", state);
                      params.delete("city");
                      params.delete("path");
                      setSearchParams(params);
                    }}
                    placeholder="State"
                    isLoading={loadingStates}
                    isSearchable
                    isClearable
                    isDisabled={!selectedCountry}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={{
                      container: selectContainerDesktop,
                      ...portalStyles,
                    }}
                  />

                  {/* City */}
                  <Select
                    classNamePrefix="custom-select"
                    options={cityOptions}
                    value={
                      cityOptions?.find(
                        (c) => c.value === Number(selectedCity),
                      ) || null
                    }
                    onChange={(val) => {
                      const city = val?.value || "";
                      setSelectedCity(city);
                      updateParam("city", city);
                    }}
                    placeholder="City"
                    isSearchable
                    isClearable
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={{
                      container: selectContainerDesktop,
                      ...portalStyles,
                    }}
                  />
                </div>
                <div className="md:hidden grid grid-cols-1 gap-2 w-full">
                  {/* Country */}
                  <Select
                    classNamePrefix="custom-select"
                    options={countryOptions}
                    value={countryOptions?.find(
                      (c) => c.value === selectedCountry,
                    )}
                    onChange={(val) => {
                      const country = val?.value || "";
                      setSelectedCountry(country);
                      setSelectedState("");
                      setSelectedCity("");

                      const params = new URLSearchParams(searchParams);
                      params.set("country", country);
                      params.delete("state");
                      params.delete("city");
                      params.delete("path");
                      setSearchParams(params);
                    }}
                    placeholder="Country"
                    isSearchable
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={{
                      container: selectContainerMobile,
                      ...portalStyles,
                    }}
                  />

                  {/* State */}
                  <Select
                    classNamePrefix="custom-select"
                    options={stateOptions}
                    value={
                      stateOptions?.find(
                        (c) => c.value === Number(selectedState),
                      ) || null
                    }
                    onChange={(val) => {
                      const state = val?.value || "";
                      setSelectedState(state);

                      setSelectedCity("");

                      const params = new URLSearchParams(searchParams);
                      params.set("state", state);
                      params.delete("city");
                      params.delete("path");
                      setSearchParams(params);
                    }}
                    placeholder="State"
                    isLoading={loadingStates}
                    isSearchable
                    isClearable
                    isDisabled={!selectedCountry}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={{
                      container: selectContainerMobile,
                      ...portalStyles,
                    }}
                  />

                  {/* City */}
                  <Select
                    classNamePrefix="custom-select"
                    options={cityOptions}
                    value={
                      cityOptions?.find(
                        (c) => c.value === Number(selectedCity),
                      ) || null
                    }
                    onChange={(val) => {
                      const city = val?.value || "";
                      setSelectedCity(city);
                      updateParam("city", city);
                    }}
                    placeholder="City"
                    isSearchable
                    isClearable
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={{
                      container: selectContainerMobile,
                      ...portalStyles,
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {!isDetail ? (
        <div className="mx-auto  max-w-[1380px] px-2 sm:px-4 max-md:py-2.5 pb-10">
          <>
            {/* ✅ View switch row */}

            <div className="min-[370px]:flex items-center gap-2">
              {/* Search */}
              <div className="relative w-full min-[370px]:w-[300px]">
                <Search
                  width={18}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-[14px]"
                />

                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search..."
                  className="
        w-full
        pl-9 pr-3 py-[7px]
        text-[13px]
        border border-gray-200
        rounded-lg
        bg-white
        shadow-sm
        transition
        focus:outline-none
        focus:border-orange-500
        focus:ring-2 focus:ring-orange-100
        placeholder:text-gray-400
      "
                />
              </div>

              {/* View Switch */}
              <ViewSwitch value={viewMode} onChange={updateView} />
            </div>

            {/* ✅ Content */}
            {viewMode === "card" ? (
              listFetch || listLoading ? (
                <div className="text-center py-10 text-gray-500">
                  Loading...
                </div>
              ) : listData?.data?.length > 0 ? (
                <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                  {listData?.data?.map((item, i) => (
                    <PremiumCard
                      key={i}
                      item={item}
                      module={module || DEFAULT_MODULE}
                    />
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <p className="text-lg font-semibold text-gray-600">
                    No records found
                  </p>
                  <p className="text-sm text-gray-400 mt-1">
                    Try changing your search or check back later.
                  </p>
                </div>
              )
            ) : (
              <div className="overflow-hidden rounded-3xl border border-orange-100 bg-white/70 shadow-sm">
                {/* Map Header */}
                {isMobile ? (
                  <MapMobile
                    country_name={country_name?.label}
                    state_name={state_name?.label}
                    city_name={city_name?.label}
                    search={debouncedSearch}
                  />
                ) : (
                  <MapComponent
                    country_name={country_name?.label}
                    state_name={state_name?.label}
                    city_name={city_name?.label}
                    search={debouncedSearch}
                  />
                )}
                ;
              </div>
            )}
          </>
        </div>
      ) : (
        <Outlet />
      )}
    </div>
  );
};

export const ViewSwitch = ({ value = "card", onChange }) => {
  const Item = ({ id, label, Icon }) => {
    const active = value === id;
    return (
      <button
        type="button"
        onClick={() => onChange(id)}
        className={[
          "inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[12px] font-semibold transition",
          "focus:outline-none focus:ring-2 focus:ring-orange-300",
          active
            ? "bg-white text-orange-800 shadow-sm border border-orange-100"
            : "text-gray-600 hover:text-orange-700 hover:bg-white/60",
        ].join(" ")}
      >
        <Icon size={14} className={active ? "opacity-100" : "opacity-80"} />
        {label}
      </button>
    );
  };

  return (
    <div
      className="
        w-full
        flex items-center justify-end gap-3

        px-3 py-2
      "
    >
      {/* Right: compact toggle */}
      <div className="inline-flex items-center rounded-xl bg-orange-50/60 p-1">
        <Item id="card" label="Card" Icon={LayoutGrid} />
        <Item id="map" label="Map" Icon={MapIcon} />
      </div>
    </div>
  );
};

const PremiumCard = ({ item, module }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const title =
    module === "organizations"
      ? item?.site_name || "—"
      : module === "channel_partners"
        ? item?.department_name
        : item?.name || "—";
  const desc = stripHtml(item?.homepage_title || item?.description || "");
  const img = item?.logo || item?.picture;

  const orgName = item?.site_name || "-";
  const partnerName = item?.channel_partner || "-";
  const email = module === "channel_partners" ? "-" : item?.email || "—";
  const phone = item?.telephone || item?.telephone || "-";
  const address = [item?.city_name, item?.state_name]
    .filter(Boolean)
    .join(", ");

  const sevaks = item?.sevaks || [];
  const visibleSevaks = sevaks.slice(0, 1);
  const extraCount = sevaks.length - visibleSevaks.length;

  return (
    <article
      className="group flex h-full flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white
      transition-all duration-300 hover:border-orange-300
      hover:shadow-[0_15px_45px_rgba(0,0,0,0.08)]"
    >
      {/* Image */}
      {module === "sevaks" ? (
        <>
          <div className="relative flex justify-center flex-col items-center gap-3 py-3  bg-orange-50 rounded-t-2xl">
            {img ? (
              <img
                src={img}
                alt={title}
                className="
        w-28 h-28 md:w-32 md:h-32
        rounded-full
        object-cover
        object-center
        border-4 border-white
        shadow-md
        transition-transform duration-300
        group-hover:scale-105
      "
                onError={(e) => (e.currentTarget.style.display = "none")}
              />
            ) : (
              <div
                className="
      w-28 h-28 md:w-36 md:h-36
      rounded-full
      bg-orange-100
      flex items-center justify-center
      text-orange-600 font-bold text-lg
      border-4 border-white
      shadow-md
       capitalize
    "
              >
                {title?.charAt(0)}
              </div>
            )}
            <h3 className="text-[16px] font-semibold text-[#9d2e43] text-center capitalize leading-snug line-clamp-2">
              {title}
            </h3>
          </div>
        </>
      ) : (
        <>
          <div className="relative h-40 xl:h-44 overflow-hidden bg-gray-50">
            {img ? (
              <img
                src={img}
                alt={title}
                loading="lazy"
                className="w-full h-full object-fill object-center transition duration-500 group-hover:scale-[1.05]"
                onError={(e) => (e.currentTarget.style.display = "none")}
              />
            ) : (
              <div className="h-full w-full grid place-items-center text-xs text-gray-400 capitalize">
                {title?.charAt(0)}
              </div>
            )}

            {/* subtle overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/5 to-transparent opacity-0 group-hover:opacity-100 transition" />
          </div>
        </>
      )}

      {/* Content */}
      <div className="flex flex-1 flex-col p-4">
        {/* Title */}
        {module !== "sevaks" && (
          <h3 className="text-[16px] mb-2 font-semibold text-[#9d2e43] text-center capitalize leading-snug line-clamp-2">
            {title}
          </h3>
        )}

        {/* Info Section */}
        {module !== "organizations" && (
          <div className=" grid grid-cols-2 gap-x-4 gap-y-2 text-[13px] font-medium text-gray-700">
            {module === "sevaks" && (
              <>
                <div className="flex items-center gap-2 min-w-0">
                  <FiMail className="text-gray-400 text-[14px]" />
                  <span className="truncate">{email}</span>
                </div>
                <div className="flex items-center gap-2 min-w-0">
                  <FiPhone className="text-gray-400 text-[14px]" />
                  <span className="truncate">{phone}</span>
                </div>
              </>
            )}
            <div className="min-w-0">
              <div className="flex items-center gap-1 text-[12px] font-semibold text-orange-600">
                <Building2 size={14} />
                <span>Organization</span>
              </div>

              <p className="text-[13px] font-medium text-gray-700 break-words ml-4">
                {orgName}
              </p>
            </div>
            {module === "channel_partners" && (
              <div className="min-w-0">
                <div className="flex items-center gap-1 text-[12px] font-semibold text-orange-600">
                  <FiUsers size={14} />
                  <span>Sevaks</span>
                </div>

                <div className="text-[13px] text-gray-700 ml-4">
                  {visibleSevaks.map((s, i) => (
                    <span key={i}>
                      {s.name}
                      {i !== visibleSevaks.length - 1 && ", "}
                    </span>
                  ))}

                  {extraCount > 0 && (
                    <span className="text-orange-600 font-medium">
                      {" "}
                      +{extraCount} more
                    </span>
                  )}
                </div>
              </div>
            )}
            {module === "sevaks" && (
              <div className="min-w-0">
                <div className="flex items-center gap-1 text-[12px] font-semibold text-orange-600">
                  <Handshake size={14} />
                  <span>VS Center</span>
                </div>

                <p className="text-[13px] font-medium text-gray-700 break-words capitalize ml-4">
                  {partnerName}
                </p>
              </div>
            )}{" "}
          </div>
        )}

        {module === "sevaks" && address && (
          <div className="flex items-center gap-2 min-w-0 text-[13px] font-medium text-gray-700 mt-1">
            <FiMapPin className="text-gray-400 text-[14px]" />
            <span className="truncate">{address}</span>
          </div>
        )}

        {/* Description */}
        {module === "organizations" && desc && (
          <p className="mt-1 text-[13.5px] leading-[1.6] text-gray-600 line-clamp-3">
            {desc}
          </p>
        )}

        {/* Actions */}
        <div className="mt-auto pt-4 flex items-center justify-between">
          <button
            type="button"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md
              bg-orange-50 text-orange-600 font-medium text-[12.5px]
              hover:bg-orange-100 transition"
            onClick={() => {
              const qs = searchParams.toString(); // keep country/state/city...;

              navigate(
                `/virtual-sevak/${module}/${toSlug(title)}-${item?.id}${qs ? `?${qs}` : ""}`,
              );
            }}
          >
            Read More →
          </button>

          {item?.subdomain ? (
            <a
              href={item.subdomain}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md
              bg-gray-100 text-gray-700 font-medium text-[12.5px]
              hover:bg-gray-200 transition"
            >
              Visit Site ↗
            </a>
          ) : (
            <span />
          )}
        </div>
      </div>
    </article>
  );
};
function stripHtml(html, limit = 160) {
  if (!html) return "";

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const text = doc.body.textContent || "";

  return text.length > limit ? text.slice(0, limit).trim() + "…" : text.trim();
}

export default VirtualLayout;
